import Icon from '@ant-design/icons';
import {
    Image,
    Checkbox,
    Button,
    Col,
    Form,
    FormInstance,
    Input,
    InputNumber,
    message,
    Row,
    Space,
    Tabs,
    Select,
    DatePicker,
    Switch,
} from 'antd';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Link, RouteComponentProps } from 'react-router-dom';
import userApi from '../../apis/UserApi';
import LayoutComponent from '../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../context/CustomContext';
import { User } from '../../model/Entities';
import { genders, grades, years } from '../../model/Types';
import { ReactComponent as SaveSvg } from '../../resources/images/save.svg';
import notificationService from '../../services/NotificationService';

const { TextArea } = Input;
const { TabPane } = Tabs;

class EditProfilePage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: true });
            const userId = this.context.auth!.id;
            const user = await userApi.get(userId);

            this.setState({ user, loading: false });
            this.formRef.current!.setFieldsValue(user);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    save = async (values: any) => {
        try {
            this.setState({ loading: true });
            let user: User = Object.assign({}, this.state.user, values);
            user = await userApi.update(user);
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));

            this.setState({ user });
        } catch (error) {
            notificationService.displayError(error, this.props.intl, [
                { status: 409, message: 'user.status.duplicate' },
            ]);
        } finally {
            this.setState({ loading: false });
        }
    };

    /*** COMPONENTS ***/

    renderPositionsAux = (): React.ReactElement | undefined => {
        return (
            <Col xs={24} md={6}>
                <Form.Item label={<FormattedMessage id="player.edit.sports.positions" />}>
                    <Button shape="round">
                        <FormattedMessage id="player.edit.sports.positions.catcher" />
                    </Button>
                    <Button shape="round">
                        <FormattedMessage id="player.edit.sports.positions.centerFielder" />
                    </Button>
                    <Button shape="round">
                        <FormattedMessage id="player.edit.sports.positions.designatedHitter" />
                    </Button>
                    <Button shape="round">
                        <FormattedMessage id="player.edit.sports.positions.firstBase" />
                    </Button>
                    <Button shape="round">
                        <FormattedMessage id="player.edit.sports.positions.leftFielder" />
                    </Button>
                    <Button shape="round">
                        <FormattedMessage id="player.edit.sports.positions.pitcher" />
                    </Button>
                    <Button shape="round">
                        <FormattedMessage id="player.edit.sports.positions.rightFielder" />
                    </Button>
                    <Button shape="round">
                        <FormattedMessage id="player.edit.sports.positions.shortStop" />
                    </Button>
                    <Button shape="round">
                        <FormattedMessage id="player.edit.sports.positions.thirdBase" />
                    </Button>
                    <Button shape="round">
                        <FormattedMessage id="player.edit.sports.positions.catcher" />
                    </Button>
                </Form.Item>
            </Col>
        );
    };

    renderPositions = (desktop: boolean): React.ReactElement | undefined => {
        if (desktop) {
            return this.renderPositionsAux();
        } else {
            return <Row gutter={[28, 0]}>{this.renderPositionsAux()}</Row>;
        }
    };

    renderPrivacy = (desktop: boolean): React.ReactElement | undefined => {
        if (desktop) {
            return (
                <>
                    <Button shape="round">
                        <FormattedMessage id="player.edit.academic.private" />
                    </Button>
                    <Button shape="round">
                        <FormattedMessage id="player.edit.academic.public" />
                    </Button>
                </>
            );
        } else {
            return (
                <Switch
                    checkedChildren={<FormattedMessage id="player.edit.academic.public" />}
                    unCheckedChildren={<FormattedMessage id="player.edit.academic.private" />}
                    defaultChecked
                />
            );
        }
    };

    renderAcademicDesktop = (desktop: boolean): React.ReactElement | undefined => {
        if (desktop) {
            return (
                <>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="player.edit.academic.coreGpa" />} name="coreGpa">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.academic.collegeCredits" />}
                                name="collegeCredits"
                            >
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.academic.classRank" />}
                                name="classRank"
                            >
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.academic.classSize" />}
                                name="classSize"
                            >
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            );
        }
    };

    renderAccountDesktop = (desktop: boolean): React.ReactElement | undefined => {
        const gendersOptions = genders.map((gender) => (
            <Select.Option key={gender} value={gender}>
                <FormattedMessage id={gender} />
            </Select.Option>
        ));

        if (desktop) {
            return (
                <>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="player.edit.account.email" />} name="accEmail">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={4}>
                            <Form.Item label={<FormattedMessage id="player.edit.account.birth" />} name="accBirthDate">
                                <DatePicker style={{ width: 150 }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={4}>
                            <Form.Item label={<FormattedMessage id="player.edit.account.gender" />} name="accGender">
                                <Select style={{ width: 150 }}>{gendersOptions}</Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={2}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.account.heightFt" />}
                                name="accHeightFt"
                            >
                                <InputNumber min={1} max={10} defaultValue={1} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={2}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.account.heightInches" />}
                                name="accHeightInches"
                            >
                                <InputNumber min={1} max={80} defaultValue={30} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={2}>
                            <Form.Item name="accPublic">
                                <Checkbox>
                                    <FormattedMessage id="player.edit.account.public" />
                                </Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={2}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.account.weightLbs" />}
                                name="accWeightLbs"
                            >
                                <InputNumber min={1} max={500} defaultValue={100} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={2}>
                            <Form.Item name="accPublic2">
                                <Checkbox>
                                    <FormattedMessage id="player.edit.account.public" />
                                </Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.account.parentName" />}
                                name="accParentName"
                            >
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.account.parentEmail" />}
                                name="accParentEmail"
                            >
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.account.parentPhone" />}
                                name="accParentPhone"
                            >
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            );
        }
    };

    renderContent = (desktop: boolean): React.ReactElement | undefined => {
        const { loading } = this.state;

        let desk, mobile;
        if (desktop) {
            desk = this.renderPositions(desktop);
        } else {
            mobile = this.renderPositions(desktop);
        }

        const gradeOptions = grades.map((grade) => (
            <Select.Option key={grade} value={grade}>
                <FormattedMessage id={grade} />
            </Select.Option>
        ));

        const yearOptions = years.map((year) => (
            <Select.Option key={year} value={year}>
                <FormattedMessage id={year} />
            </Select.Option>
        ));

        return (
            <>
                <div>
                    <h1>
                        <FormattedMessage id="player.edit.header" />
                    </h1>
                </div>

                <div>
                    <FormattedMessage id="player.edit.description" />
                </div>

                <Tabs defaultActiveKey="1" type="card">
                    <TabPane tab={<FormattedMessage id="player.edit.sports" />} key="1">
                        <Form ref={this.formRef} colon={false} layout="vertical">
                            <Row gutter={[28, 0]}>
                                <Col xs={10} md={8}>
                                    <div>
                                        <h1>
                                            <FormattedMessage id="player.edit.sports.baseball" />
                                        </h1>
                                    </div>
                                </Col>
                                <Col xs={10} md={8}>
                                    <Button shape="round">
                                        <FormattedMessage id="player.edit.sports.addSport" />
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                        <Form ref={this.formRef} colon={false} layout="vertical">
                            <Row gutter={[28, 0]}>
                                <Col xs={24} md={8}>
                                    <Form.Item label={<FormattedMessage id="player.edit.bio" />} name="bio">
                                        <TextArea rows={4} />
                                    </Form.Item>
                                </Col>
                                {desk}
                            </Row>
                            <Row gutter={[28, 0]}>
                                <Col xs={24} md={8}>
                                    <Form.Item label={<FormattedMessage id="player.edit.offers" />} name="offers">
                                        <TextArea rows={4} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={[28, 0]}>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label={<FormattedMessage id="player.edit.comittedTo" />}
                                        name="comittedTo"
                                    >
                                        <Input maxLength={100} size="large" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={[28, 0]}>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label={<FormattedMessage id="player.edit.jerseyNumber" />}
                                        name="jerseyNumber"
                                    >
                                        <InputNumber min={1} max={50} defaultValue={10} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {mobile}
                        </Form>
                    </TabPane>

                    <TabPane tab={<FormattedMessage id="player.edit.academic" />} key="2">
                        <Form ref={this.formRef} colon={false} layout="vertical">
                            <Row gutter={[28, 0]}>
                                <Col xs={10} md={8}>
                                    <div>
                                        <h1>
                                            <FormattedMessage id="player.edit.academic.info" />
                                        </h1>
                                    </div>
                                </Col>
                                <Col xs={10} md={8}>
                                    {this.renderPrivacy(desktop)}
                                </Col>
                            </Row>
                        </Form>
                        <Form ref={this.formRef} colon={false} layout="vertical">
                            <Row gutter={[28, 0]}>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label={<FormattedMessage id="player.edit.academic.college" />}
                                        name="college"
                                    >
                                        <Input maxLength={100} size="large" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item name="ncaa">
                                        <Checkbox>
                                            <FormattedMessage id="player.edit.academic.ncaa" />
                                        </Checkbox>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={[28, 0]}>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label={<FormattedMessage id="player.edit.academic.grade" />}
                                        name="grade"
                                    >
                                        <Select style={{ width: 395 }}>{gradeOptions}</Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label={<FormattedMessage id="player.edit.academic.graduationYear" />}
                                        name="graduationYear"
                                    >
                                        <Select style={{ width: 395 }}>{yearOptions}</Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={[28, 0]}>
                                <Col xs={24} md={8}>
                                    <Form.Item label={<FormattedMessage id="player.edit.academic.act" />} name="act">
                                        <Input maxLength={100} size="large" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item label={<FormattedMessage id="player.edit.academic.sat" />} name="sat">
                                        <Input maxLength={100} size="large" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {this.renderAcademicDesktop(desktop)}
                        </Form>
                    </TabPane>

                    <TabPane tab={<FormattedMessage id="player.edit.account" />} key="3">
                        <Form ref={this.formRef} colon={false} layout="vertical">
                            <Row gutter={[28, 0]}>
                                <Col xs={10} md={8}>
                                    <div>
                                        <h1>
                                            <FormattedMessage id="player.edit.account.personal" />
                                        </h1>
                                    </div>
                                </Col>
                                <Col xs={10} md={8}>
                                    {this.renderPrivacy(desktop)}
                                </Col>
                            </Row>
                            <Row gutter={[28, 0]}>
                                <Col xs={24} md={8}>
                                    <Image
                                        width={200}
                                        height={200}
                                        src="error"
                                        fallback="data:image/png;base64,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"
                                    />
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label={<FormattedMessage id="player.edit.account.twitterHandle" />}
                                        name="twitterHandle"
                                    >
                                        <Input maxLength={100} size="large" />
                                    </Form.Item>
                                    <Form.Item
                                        label={<FormattedMessage id="player.edit.account.personalUrl" />}
                                        name="personalUrl"
                                    >
                                        <Input maxLength={100} size="large" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>

                        <div>
                            <h1>
                                <FormattedMessage id="player.edit.account.info" />
                            </h1>
                        </div>

                        <Form ref={this.formRef} colon={false} layout="vertical">
                            <Row gutter={[28, 0]}>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label={<FormattedMessage id="player.edit.account.firstName" />}
                                        name="accFirstName"
                                    >
                                        <Input maxLength={100} size="large" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label={<FormattedMessage id="player.edit.account.lastName" />}
                                        name="accLastName"
                                    >
                                        <Input maxLength={100} size="large" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {this.renderAccountDesktop(desktop)}
                        </Form>
                    </TabPane>
                </Tabs>

                <Form.Item className="buttons">
                    <Space size={15}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            icon={<Icon component={SaveSvg} />}
                            loading={loading}
                        >
                            <FormattedMessage id="settings.button.save" tagName="span" />
                        </Button>
                        <Link to="/rosters">
                            <Button type="text" size="large">
                                <FormattedMessage id="settings.button.back" tagName="span" />
                            </Button>
                        </Link>
                    </Space>
                </Form.Item>
            </>
        );
    };

    render() {
        const playerId = this.props.match.params.id;
        return <LayoutComponent page="player-edit" content={this.renderContent} playerId={playerId} />;
    }
}
export default injectIntl(EditProfilePage);

type ParamsType = { id: string };
interface Props extends RouteComponentProps<ParamsType>, WrappedComponentProps {}

interface State {
    user?: User;
    loading?: boolean;
}
